import {
  Component,
  Input,
  OnInit,
  ViewChild,
  TemplateRef,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  LearmoModalRef,
  LearmoModalService,
} from 'src/app/shared/services/learmo-modal.service';
import { ApiService } from 'src/app/shared/services/lsg.api.service';
import { PageItem } from 'src/app/models/portal';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { PromptingService } from 'src/app/shared/services/prompting.service';

@Component({
  selector: 'app-legal-page-editor-modal',
  templateUrl: './legal-page-editor-modal.component.html',
  styleUrls: ['./legal-page-editor-modal.component.css'],
})
export class LegalPageEditorModalComponent implements OnInit {
  @Input() page: PageItem;
  @Output() onContentUpdated: EventEmitter<PageItem> =
    new EventEmitter<PageItem>();

  modalRef: LearmoModalRef;
  processing = false;
  content = '';
  initialContent = '';
  variables: { name: string; value: string; label?: string }[] = [];

  @ViewChild('modalContent') modalContent: TemplateRef<any>;
  @ViewChild('modalFooter') modalFooter: TemplateRef<any>;

  constructor(
    private modalService: LearmoModalService,
    private api: ApiService,
    private toastr: ToastrService,
    private prompting: PromptingService,
  ) {}

  ngOnInit(): void {}

  async open() {
    this.processing = true;
    try {
      // Fetch fresh page content
      const res: any = await lastValueFrom(this.api.getPage(this.page.id));
      this.page = res.page || res; // Adjust based on API response structure
      this.content = this.page.contentMarkdown || '';
      this.initialContent = this.content;

      // Fetch website config for variables
      const website: any = await lastValueFrom(this.api.getCurrentWebsite());
      this.computeVariables(website);

      this.modalRef = this.modalService.open(this.modalContent, {
        title: `Edit ${this.page.title}`,
        footer: this.modalFooter,
        size: 'xl', // Use larger modal for editor
      });
    } catch (err: any) {
      this.toastr.error('Failed to load page content');
    } finally {
      this.processing = false;
    }
  }

  close() {
    if (this.content !== this.initialContent) {
      this.prompting
        .confirm({
          title: 'Unsaved Changes',
          html: 'You have unsaved changes. Are you sure you want to discard them?',
          confirmButtonText: 'Discard',
          cancelButtonText: 'Keep Editing',
          icon: 'warning',
        })
        .then((res) => {
          if (res.isConfirmed) {
            this.modalRef.dismiss();
          }
        });
    } else {
      this.modalRef.dismiss();
    }
  }

  async save() {
    this.processing = true;
    try {
      const updateRes: any = await lastValueFrom(
        this.api.updatePageSettings(this.page.id, {
          contentMarkdown: this.content,
        }),
      );

      this.toastr.success('Page content updated successfully');

      const updatedPage = updateRes.page || updateRes;

      this.onContentUpdated.emit(
        updatedPage || { ...this.page, content: this.content },
      );
      this.initialContent = this.content; // Reset initial content to avoid dirty check on close
      this.modalRef.close();
    } catch (err: any) {
      this.toastr.error(err?.message || 'Failed to update content');
    } finally {
      this.processing = false;
    }
  }

  async restoreTemplate() {
    const confirm = await this.prompting.confirm({
      title: 'Restore Default Template?',
      html: 'This will replace your current content with the default legal template. This action cannot be undone.',
      confirmButtonText: 'Restore',
      cancelButtonText: 'Cancel',
      icon: 'warning',
    });

    if (confirm.isConfirmed) {
      this.processing = true;
      try {
        const res: any = await lastValueFrom(
          this.api.restoreLegalTemplate(this.page.id),
        );

        // API now returns the page object
        const updatedPage = res.page || res;

        this.content = updatedPage.contentMarkdown || updatedPage.content || '';

        this.toastr.success('Default template restored');
      } catch (err: any) {
        this.toastr.error(err?.message || 'Failed to restore template');
      } finally {
        this.processing = false;
      }
    }
  }

  computeVariables(website: any) {
    const identity = website.identity || website.portalIdentity || {};
    const legal = identity.legal_business_details || {};

    let refundPeriod = legal.refundPeriod || '30';
    if (refundPeriod === 'Custom') {
      refundPeriod = legal.customDays ? `${legal.customDays} days` : '30 days';
    } else if (
      refundPeriod &&
      !refundPeriod.toLowerCase().includes('day') &&
      !isNaN(Number(refundPeriod))
    ) {
      refundPeriod = `${refundPeriod} days`;
    }

    const lastUpdated = this.page?.updatedAt
      ? new Date(this.page.updatedAt).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : new Date().toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });

    const varsMap = {
      siteName: website.label || identity.title || 'Our Site',
      siteUrl: website.siteUrl || '',
      lastUpdated: lastUpdated,
      legalAddress: legal.businessAddress || '',
      supportEmail: legal.contactEmail || '',
      refundPeriod: refundPeriod,
      legalVenue: legal.legalVenue || 'your local jurisdiction',
      companyName: website.label || identity.title || 'Our Company',
      contactEmail: legal.contactEmail || '',
      siteDescription: identity.description || '',
      replyEmail: website.senderMailConfig?.replyEmail || '',
    };

    this.variables = Object.entries(varsMap).map(([name, value]) => ({
      name,
      value: (value as string) || '',
      label: this.getLabelForVar(name),
    }));
  }

  getLabelForVar(name: string): string {
    const labels: Record<string, string> = {
      siteName: 'Site Name',
      siteUrl: 'Site URL',
      lastUpdated: 'Last Updated Date',
      legalAddress: 'Business Address',
      supportEmail: 'Support Email',
      refundPeriod: 'Refund Period',
      legalVenue: 'Legal Venue (Jurisdiction)',
      companyName: 'Company Name',
      contactEmail: 'Contact Email',
      siteDescription: 'Site Description',
      replyEmail: 'Reply-To Email',
    };
    return labels[name] || name;
  }
}
